/**
 * Per-day business hours helpers (24x7 = No).
 * Matches nested shape used by agreements and MSD sync (monStartHour, tueStartHour, …).
 */

export const DAY_CONFIG = [
  { label: "Monday", dayKey: "monday", startKey: "monStartHour", endKey: "monEndHour" },
  { label: "Tuesday", dayKey: "tuesday", startKey: "tueStartHour", endKey: "tueEndHour" },
  { label: "Wednesday", dayKey: "wednesday", startKey: "wedStartHour", endKey: "wedEndHour" },
  { label: "Thursday", dayKey: "thursday", startKey: "thuStartHour", endKey: "thuEndHour" },
  { label: "Friday", dayKey: "friday", startKey: "friStartHour", endKey: "friEndHour" },
  { label: "Saturday", dayKey: "saturday", startKey: "satStartHour", endKey: "satEndHour" },
  { label: "Sunday", dayKey: "sunday", startKey: "sunStartHour", endKey: "sunEndHour" },
];

/** Treats "Yes" as enabled; anything else (NO, No, etc.) as disabled. */
export function isBusinessDayEnabled(dayValue) {
  return dayValue === "Yes";
}

const EMPTY_PLACEHOLDER = " ";

function trimOrEmpty(v) {
  if (v == null) return "";
  return String(v).trim();
}

/**
 * Prefer stored per-day hour; if missing and day is enabled, use global start/end; else placeholder.
 */
export function getPerDayStartEndInitial(bhd, dayKey, startKey, endKey, globalStart, globalEnd, enabled) {
  const s = trimOrEmpty(bhd?.[startKey]);
  const e = trimOrEmpty(bhd?.[endKey]);
  const en = isBusinessDayEnabled(enabled);
  return {
    [startKey]: s || (en ? trimOrEmpty(globalStart) : EMPTY_PLACEHOLDER) || EMPTY_PLACEHOLDER,
    [endKey]: e || (en ? trimOrEmpty(globalEnd) : EMPTY_PLACEHOLDER) || EMPTY_PLACEHOLDER,
  };
}

/**
 * Build flat initial values for all *StartHour / *EndHour from businessHourData + globals + day flags.
 */
export function buildPerDayHourFieldsInitial({
  businessHourData: bhd = {},
  businessHourStart = "",
  businessHourEnd = "",
  sunday,
  monday,
  tuesday,
  wednesday,
  thursday,
  friday,
  saturday,
}) {
  const flags = { sunday, monday, tuesday, wednesday, thursday, friday, saturday };
  const out = {};
  DAY_CONFIG.forEach(({ dayKey, startKey, endKey }) => {
    Object.assign(
      out,
      getPerDayStartEndInitial(bhd, dayKey, startKey, endKey, businessHourStart, businessHourEnd, flags[dayKey])
    );
  });
  return out;
}

/**
 * Nested businessHourData for API / agreement payload (same keys as signed agreement submit).
 * Accepts camelCase or legacy lowercase proposal field names.
 */
export function buildBusinessHourDataFromForm(data) {
  const raw = data || {};
  const d = {
    ...raw,
    sunday: raw.sunday,
    monday: raw.monday,
    tuesday: raw.tuesday,
    wednesday: raw.wednesday,
    thursday: raw.thursday ?? raw.thurday,
    friday: raw.friday,
    saturday: raw.saturday,
    monStartHour: raw.monStartHour,
    monEndHour: raw.monEndHour,
    tueStartHour: raw.tueStartHour,
    tueEndHour: raw.tueEndHour,
    wedStartHour: raw.wedStartHour,
    wedEndHour: raw.wedEndHour,
    thuStartHour: raw.thuStartHour,
    thuEndHour: raw.thuEndHour,
    friStartHour: raw.friStartHour,
    friEndHour: raw.friEndHour,
    satStartHour: raw.satStartHour,
    satEndHour: raw.satEndHour,
    sunStartHour: raw.sunStartHour,
    sunEndHour: raw.sunEndHour,
  };
  return {
    sunday: d.sunday ?? "No",
    monday: d.monday ?? "NO",
    tuesday: d.tuesday ?? "NO",
    wednesday: d.wednesday ?? "NO",
    thursday: d.thursday ?? "NO",
    friday: d.friday ?? "NO",
    saturday: d.saturday ?? "No",
    monStartHour: d.monStartHour ?? " ",
    monEndHour: d.monEndHour ?? " ",
    tueStartHour: d.tueStartHour ?? " ",
    tueEndHour: d.tueEndHour ?? " ",
    wedStartHour: d.wedStartHour ?? " ",
    wedEndHour: d.wedEndHour ?? " ",
    thuStartHour: d.thuStartHour ?? " ",
    thuEndHour: d.thuEndHour ?? "",
    friStartHour: d.friStartHour ?? "",
    friEndHour: d.friEndHour ?? "",
    satStartHour: d.satStartHour ?? "",
    satEndHour: d.satEndHour ?? "",
    sunStartHour: d.sunStartHour ?? "",
    sunEndHour: d.sunEndHour ?? "",
  };
}

/**
 * Copy global start/end to each enabled day only.
 */
export function applyGlobalToEnabledDays(setFieldValue, values, globalStart, globalEnd) {
  DAY_CONFIG.forEach(({ dayKey, startKey, endKey }) => {
    if (isBusinessDayEnabled(values[dayKey])) {
      setFieldValue(startKey, globalStart ?? "");
      setFieldValue(endKey, globalEnd ?? "");
    }
  });
}

/**
 * When user enables a day, fill start/end from global if still empty.
 */
export function fillDayFromGlobalIfEmpty(setFieldValue, values, dayKey, globalStart, globalEnd) {
  if (!isBusinessDayEnabled(values[dayKey])) return;
  const { startKey, endKey } = DAY_CONFIG.find((c) => c.dayKey === dayKey) || {};
  if (!startKey || !endKey) return;
  const s = trimOrEmpty(values[startKey]);
  const e = trimOrEmpty(values[endKey]);
  if (!s && globalStart) setFieldValue(startKey, globalStart);
  if (!e && globalEnd) setFieldValue(endKey, globalEnd);
}

/** Supports both camelCase and legacy lowercase field names on proposal forms. */
export function getGlobalBusinessHourStartEnd(values) {
  const d = values || {};
  return {
    start: trimOrEmpty(d.businessHourStart ?? d.businesshourstart),
    end: trimOrEmpty(d.businessHourEnd ?? d.businesshourend),
  };
}

/**
 * Merge validateForm errors for businessHour === "No": global + per enabled day.
 * @param {object} values form values
 * @param {object} errors existing errors (mutated)
 */
/**
 * Title-case each alphabetic word (PDF + preview) so day names match capitalize styling.
 */
export function capitalizeDisplayWords(str) {
  if (!str || typeof str !== "string") return str;
  return str.replace(/[A-Za-z]+/g, (word) => {
    if (!word.length) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

/** Populated `floor.name`, or legacy string id; omit when unknown. */
export function formatFloorForProposal(row) {
  if (!row) return null;
  const f = row.floor;
  if (f && typeof f === "object" && f.name != null && String(f.name).trim() !== "") {
    return String(f.name).trim();
  }
  if (typeof f === "string" && f.trim() !== "") return f.trim();
  return null;
}

/** PDF / detail row: global summary plus per-day lines when any day differs from global. */
export function formatBusinessDaysHoursDisplay(rowValue, selectedRange, totalHour) {
  if (!rowValue) return "";
  let out;
  if (rowValue.businessHour === "Yes") {
    const h = rowValue?.totalHour === "" || rowValue?.totalHour == null ? 24 : rowValue?.totalHour;
    out = `Monday-Sunday : ${h} Hours`;
  } else {
    const range = selectedRange || "Monday-Friday";
    const gs = trimOrEmpty(rowValue.businessHourStart);
    const ge = trimOrEmpty(rowValue.businessHourEnd);
    const base = `${range} : ${gs}-${ge},${totalHour ?? ""}`;
    const bhd = rowValue.businessHourData;
    if (!bhd) {
      out = base;
    } else {
      const gsNorm = gs;
      const geNorm = ge;
      const detailLines = [];
      DAY_CONFIG.forEach(({ label, dayKey, startKey, endKey }) => {
        if (!isBusinessDayEnabled(bhd[dayKey])) return;
        const s = trimOrEmpty(bhd[startKey]);
        const e = trimOrEmpty(bhd[endKey]);
        if (!s || !e) return;
        if (s !== gsNorm || e !== geNorm) {
          detailLines.push(`${label} ${s}–${e}`);
        }
      });
      out = detailLines.length === 0 ? base : `${base} | ${detailLines.join("; ")}`;
    }
  }
  return capitalizeDisplayWords(out);
}

export function mergeBusinessHoursPerDayErrors(values, errors = {}) {
  const bh = values.businessHour ?? values.businesshour;
  if (bh !== "No") return errors;

  const { start: gs, end: ge } = getGlobalBusinessHourStartEnd(values);
  if (!gs) errors.businessHourStart = errors.businessHourStart || "Required";
  if (!ge) errors.businessHourEnd = errors.businessHourEnd || "Required";

  DAY_CONFIG.forEach(({ dayKey, startKey, endKey }) => {
    if (!isBusinessDayEnabled(values[dayKey])) return;
    if (!trimOrEmpty(values[startKey])) errors[startKey] = errors[startKey] || "Required";
    if (!trimOrEmpty(values[endKey])) errors[endKey] = errors[endKey] || "Required";
  });
  return errors;
}
